import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { LoginFormData } from '../types/auth';
import { useAuth } from './useAuth';

// バリデーションスキーマ
const companyLoginSchema = yup.object({
  email: yup
    .string()
    .email('正しいメールアドレスの形式で入力してください')
    .required('メールアドレスは必須です'),
  password: yup
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .required('パスワードは必須です'),
  rememberMe: yup.boolean(),
});

interface UseCompanyLoginReturn {
  register: any;
  handleSubmit: any;
  errors: any;
  isSubmitting: boolean;
  isValid: boolean;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  authError: string | null;
  errorDetails: any;
  handleFieldFocus: (field: string) => void;
  resetForm: () => void;
}

export const useCompanyLogin = (): UseCompanyLoginReturn => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const { login, error: authError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: yupResolver(companyLoginSchema) as any,
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const handleFieldFocus = useCallback(
    (field: string) => {
      clearErrors(field as keyof LoginFormData);
      if (error) {
        setError(null);
        setErrorDetails(null);
      }
    },
    [clearErrors, error]
  );

  const onSubmit = useCallback(
    async (data: LoginFormData) => {
      try {
        setError(null);
        setErrorDetails(null);

        // 企業ユーザーでのログイン
        await login(data, data.rememberMe, 'company');
      } catch (err: any) {
        // エラーハンドリング
        const errorMessage = err.response?.data?.error?.message;

        if (errorMessage) {
          setError(errorMessage);
        } else if (err.message) {
          setError(err.message);
        } else {
          setError(
            'ログインに失敗しました。メールアドレスとパスワードを確認してください。'
          );
        }

        if (err.response?.data?.error) {
          setErrorDetails(err.response.data.error);
        } else if (err.details) {
          setErrorDetails(err.details);
        }
      }
    },
    [login, navigate]
  );

  const resetForm = useCallback(() => {
    reset();
    setError(null);
    setErrorDetails(null);
    setShowPassword(false);
  }, [reset]);

  return {
    register,
    handleSubmit: handleSubmit(onSubmit as any),
    errors,
    isSubmitting,
    isValid,
    showPassword,
    setShowPassword,
    authError: error,
    errorDetails,
    handleFieldFocus,
    resetForm,
  };
};
